import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { MessageSquare, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';

// On chatbaze.id: use relative URL so worker.ts handles server-side redirect to app.chatbaze.id
const AuthLink = ({ to, className, children }: { to: string; className?: string; children: React.ReactNode }) => {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const isMainDomain = hostname === 'chatbaze.id' || hostname === 'www.chatbaze.id';
  if (isMainDomain) {
    return <a href={to} className={className}>{children}</a>;
  }
  return <Link to={to} className={className}>{children}</Link>;
};

interface PublicLayoutProps {
  children: React.ReactNode;
}

const PublicLayout = ({ children }: PublicLayoutProps) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background font-sans">
      <header className="sticky top-0 left-0 right-0 z-50 w-full border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center shadow-lg shadow-primary/20">
              <MessageSquare className="h-4 w-4 text-primary-foreground" />
            </div>
            <span className="font-bold text-foreground">Chatbaze</span>
            <span className="text-xs bg-muted px-1.5 py-0.5 rounded text-muted-foreground font-medium border border-border/50">Guide</span>
          </Link>

          {user ? (
            <Link to="/dashboard">
              <Button variant="ghost" size="sm" className="font-bold">Kembali ke Dashboard</Button>
            </Link>
          ) : (
            <AuthLink to="/login">
              <Button variant="ghost" size="sm" className="font-bold">Masuk ke Dashboard</Button>
            </AuthLink>
          )}
        </div>
      </header>
      
      <main className="container mx-auto px-4 py-8 md:py-12">
        {children}
      </main>

      <footer className="border-t border-border/40 py-8 bg-muted/30 mt-12">
        <div className="container mx-auto px-4 text-center">
          <p className="text-sm text-muted-foreground">
            &copy; {new Date().getFullYear()} Chatbaze. Seluruh hak cipta dilindungi.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PublicLayout;
